import { defineStore } from 'pinia';

import { isTimeWindow } from '@/utils/timeWindows.js';

/**
 * Tenant store — owner of the active app_id and the AbortController registry.
 *
 * usePoll() (Story 2.14) registers each in-flight panel fetch's
 * AbortController with this store. setActiveTenant() aborts all of them and
 * fires the same key the polls listen to so they re-fetch with the new
 * scope. The server always re-validates tenant scope per request
 * (FR21/FR30) — the URL is for shareability, not authority.
 */
export const useTenantStore = defineStore('tenant', {
  state: () => ({
    activeAppId: null,
    abortControllers: new Set(),
    changeKey: 0,
    // Story 2.11 — active hero-panel window. Default 7d aligns con UX
    // expectation; cualquier hero panel observa este state via `usePoll`.
    timeWindow: '7d'
  }),

  getters: {
    /** The currently active tenant row, looked up against the auth scope. */
    activeTenant() {
      // Late-resolve via the global pinia. Implementation in component layer
      // can read both stores directly; this getter is kept for ergonomics.
      return this.activeAppId;
    }
  },

  actions: {
    /** Initialize from the auth scope's first tenant (or a persisted choice). */
    init(initialAppId) {
      if (initialAppId) this.activeAppId = initialAppId;
    },

    /** Register an AbortController whose fetch should be cancelled on tenant change. */
    registerAbortController(controller) {
      if (controller) this.abortControllers.add(controller);
    },

    /** Forget a controller after its fetch resolves (success or error). */
    deregisterAbortController(controller) {
      this.abortControllers.delete(controller);
    },

    /** Abort every registered controller. Used by setActiveTenant; tests call it directly. */
    abortAll(reason = 'tenant-changed') {
      for (const c of this.abortControllers) {
        try { c.abort(reason); } catch { /* noop */ }
      }
      this.abortControllers.clear();
    },

    /**
     * Switch active tenant. Aborts in-flight fetches, bumps changeKey so
     * watchers refire, and updates activeAppId.
     *
     * The caller is expected to update the URL (router push) separately —
     * keeping the store URL-agnostic so it can be used in tests without
     * a router.
     */
    setActiveTenant(appId) {
      if (appId === this.activeAppId) return;
      this.abortAll('tenant-changed');
      this.activeAppId = appId;
      this.changeKey += 1;
    },

    /**
     * Story 2.11 — switch active time window for hero panels. Aborts
     * in-flight fetches, bumps changeKey (panels listening via
     * usePoll re-fetch), updates timeWindow.
     *
     * Window enum hard-coded — debe coincidir con el server-side
     * `SUPPORTED_WINDOWS` (Story 2.5).
     */
    setTimeWindow(value) {
      if (!isTimeWindow(value)) return;
      if (value === this.timeWindow) return;
      this.abortAll('time-window-changed');
      this.timeWindow = value;
      this.changeKey += 1;
    }
  }
});
